import { PDFDocument } from 'pdf-lib';
import { DEFAULT_TOOL_SETTINGS, OBJ } from '../constants';
import { makeText, makeShape } from './objects';
import { makeTable } from './tableLayout';
import { parseHtmlTable } from './transcriptTables';
import {
    TEXT_PAD,
    ensureEditorFonts,
    measuredTextHeight,
    recomputeTable,
    wrapTextLines,
} from './drawObjects';

/**
 * Open a generated MANUAL document (the HTML the template + form values produce) in the
 * shared PDF editor.
 *
 * The manual-document PDF is a picture of that HTML, so there is no text in it to edit.
 * This flattens the same HTML into what the editor's document mode works with: a blank
 * PDF of the template's page size plus editable text boxes laid down it the way the
 * export lays the page out — same page size and margins, same sizes, alignment,
 * bold/italic/underline runs, line heights, tables and page breaks. Everything is CSS px
 * @96dpi in the HTML and PDF points in the editor, so lengths are scaled by 0.75.
 *
 * Fidelity is deliberately that of the EXPORT, not of the raw Lexical HTML: the export
 * paints every text dark, and its stylesheet makes plain `<span>`s inherit their
 * paragraph's size and family — so those inline overrides are ignored here too.
 * Pictures are not carried over, and a table's columns are equal width (the editor's
 * table object has no column widths yet).
 */

const PX = 0.75; // CSS px @96dpi → PDF points
const BASE_PX = 13; // the export's paragraph size
const FAMILY = 'Kalpurush';
const STACK = "'Kalpurush','Noto Sans Bengali','SolaimanLipi',Arial,sans-serif";
// The catalogue face behind the document's own one (what the font picker / exporter fall back to).
const CATALOGUE_FAMILY = 'Noto Sans Bengali';

// Default size (em of the paragraph size) and weight of each heading, as in the export CSS.
const HEADING_EM = { h1: 2, h2: 1.6, h3: 1.3, h4: 1.1, h5: 1, h6: 0.95 };
const HEADING_LH = { h1: 1.3, h2: 1.3, h3: 1.4, h4: 1.4, h5: 1.4, h6: 1.4 };
const BLOCK_TAGS = new Set([
    'p', 'div', 'section', 'article', 'header', 'footer', 'main', 'blockquote', 'pre',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'table', 'hr', 'form', 'figure',
]);

/** Page-number captions the export adds on every page of a multi-page stamp document. */
export const STAMP_PAGE_ROLE = 'stampPageNo';

/* ------------------------------------------------------------------ parsing */

const px = (value, parent) => {
    const m = /^(-?[\d.]+)\s*(px|em|rem|%|pt)?$/i.exec(String(value ?? '').trim());
    if (!m) return null;
    const n = parseFloat(m[1]);
    if (!Number.isFinite(n)) return null;
    switch ((m[2] || 'px').toLowerCase()) {
        case 'em': return n * parent;
        case 'rem': return n * BASE_PX;
        case '%': return (n / 100) * parent;
        case 'pt': return n / PX;
        default: return n;
    }
};

const isPlaceholder = (el) => el.classList?.contains('doc-value-filled')
    || el.classList?.contains('doc-value-unfilled')
    || el.classList?.contains('doc-placeholder-token');

/** The inline style a run inherits from `el`, as the export's stylesheet would resolve it. */
function inlineStyle(el, parent) {
    const tag = el.tagName.toLowerCase();
    const s = el.style || {};
    const out = { ...parent };

    if (tag === 'b' || tag === 'strong') out.bold = true;
    if (tag === 'i' || tag === 'em') out.italic = true;
    if (tag === 'u') out.underline = true;
    if (tag === 's' || tag === 'strike' || tag === 'del') out.strike = true;
    if (tag === 'sub' || tag === 'sup') out.px = parent.px * 0.8;

    if (s.fontWeight) {
        const w = String(s.fontWeight).toLowerCase();
        out.bold = w === 'bold' || w === 'bolder' || parseInt(w, 10) >= 600;
    }
    if (s.fontStyle) out.italic = s.fontStyle === 'italic' || s.fontStyle === 'oblique';
    const deco = `${s.textDecoration || ''} ${s.textDecorationLine || ''}`;
    if (/underline/.test(deco)) out.underline = true;
    if (/line-through/.test(deco)) out.strike = true;

    // The export CSS makes plain spans inherit their block's size, whatever Lexical put on them;
    // only placeholder values (and non-span elements such as <strong>) keep an inline size.
    const plainSpan = tag === 'span' && !isPlaceholder(el);
    if (s.fontSize && !plainSpan) {
        const size = px(s.fontSize, parent.px);
        if (size && size > 2) out.px = size;
    }
    return out;
}

/** Block-level defaults (size, weight, line height) from the export stylesheet + inline style. */
function blockDefaults(el) {
    const tag = el.tagName.toLowerCase();
    const s = el.style || {};
    const base = { px: BASE_PX, bold: false, italic: false, underline: false, strike: false };
    let lh = 1.9;
    if (HEADING_EM[tag]) {
        base.px = BASE_PX * HEADING_EM[tag];
        base.bold = true;
        lh = HEADING_LH[tag];
    }
    if (tag === 'blockquote') base.italic = true;
    if (s.fontSize) {
        const size = px(s.fontSize, BASE_PX);
        if (size && size > 2) base.px = size;
    }
    if (s.lineHeight) {
        const v = String(s.lineHeight).trim();
        if (/^[\d.]+$/.test(v)) lh = parseFloat(v);
        else {
            const abs = px(v, base.px);
            if (abs) lh = abs / base.px;
        }
    }
    if (s.fontWeight) {
        const w = String(s.fontWeight).toLowerCase();
        base.bold = w === 'bold' || w === 'bolder' || parseInt(w, 10) >= 600;
    }
    const align = (s.textAlign || el.getAttribute('align') || '').toLowerCase();
    const indent = Math.max(0,
        px(s.paddingInlineStart, base.px) ?? px(s.paddingLeft, base.px) ?? px(s.marginLeft, base.px) ?? 0);
    return {
        base,
        lh: Math.min(4, Math.max(0.8, lh)),
        align: ['left', 'center', 'right', 'justify'].includes(align) ? align : 'left',
        indent,
    };
}

/** Collect the styled runs of an element's inline content. */
function collectRuns(node, style, out) {
    for (const child of node.childNodes) {
        if (child.nodeType === 3) {
            const text = child.nodeValue.replace(/[\r\n\t]+/g, ' ').replace(/ /g, ' ');
            if (text) out.push({ text, style });
        } else if (child.nodeType === 1) {
            const tag = child.tagName.toLowerCase();
            if (tag === 'br') out.push({ text: '\n', style });
            else if (tag === 'img' || tag === 'script' || tag === 'style') continue;
            else if (child.classList?.contains('doc-value-unfilled')) {
                // A blank the user never filled: shown as a [Label] chip, click-to-replace in the editor.
                const label = (child.textContent || '').trim();
                if (label) out.push({ text: `[${label}]`, style: inlineStyle(child, style) });
            } else if (BLOCK_TAGS.has(tag) && tag !== 'li') {
                // A block nested inside inline content: keep its text on a line of its own.
                const inner = [];
                collectRuns(child, inlineStyle(child, style), inner);
                if (inner.length) {
                    out.push({ text: '\n', style });
                    out.push(...inner);
                    out.push({ text: '\n', style });
                }
            } else {
                collectRuns(child, inlineStyle(child, style), out);
            }
        }
    }
}

const sameRunStyle = (a, b) => a.bold === b.bold && a.italic === b.italic
    && a.underline === b.underline && a.strike === b.strike && a.px === b.px;

/** Runs → { text, marks, base } where `base` is the style most of the text shares. */
function toTextAndMarks(runs) {
    const merged = [];
    for (const run of runs) {
        const last = merged[merged.length - 1];
        if (last && sameRunStyle(last.style, run.style)) last.text += run.text;
        else merged.push({ text: run.text, style: run.style });
    }
    // Trailing line breaks / blanks add nothing but height.
    while (merged.length) {
        const last = merged[merged.length - 1];
        last.text = last.text.replace(/[\s]+$/, '');
        if (last.text) break;
        merged.pop();
    }
    let text = '';
    const spans = [];
    for (const run of merged) {
        spans.push({ start: text.length, end: text.length + run.text.length, style: run.style });
        text += run.text;
    }
    if (!text.trim()) return { text: '', marks: [], base: null };

    // The base style = the one covering the most characters, so only the exceptions become marks.
    const weight = new Map();
    for (const sp of spans) {
        const key = JSON.stringify([sp.style.bold, sp.style.italic, sp.style.underline, sp.style.strike, sp.style.px]);
        weight.set(key, (weight.get(key) || 0) + (sp.end - sp.start));
    }
    const [bestKey] = [...weight.entries()].sort((a, b) => b[1] - a[1])[0];
    const base = spans.find((sp) => JSON.stringify([sp.style.bold, sp.style.italic, sp.style.underline, sp.style.strike, sp.style.px]) === bestKey).style;

    const marks = [];
    for (const sp of spans) {
        if (sameRunStyle(sp.style, base)) continue;
        const style = {};
        if (sp.style.bold !== base.bold) style.bold = !!sp.style.bold;
        if (sp.style.italic !== base.italic) style.italic = !!sp.style.italic;
        if (sp.style.underline !== base.underline) style.underline = !!sp.style.underline;
        if (sp.style.strike !== base.strike) style.strike = !!sp.style.strike;
        if (sp.style.px !== base.px) style.fontSize = sp.style.px * PX;
        if (Object.keys(style).length) marks.push({ start: sp.start, end: sp.end, style });
    }
    return { text, marks, base };
}

/**
 * HTML → ordered blocks:
 *   { type:'para', text, marks, base, lh, align, indent, gapBefore, gapAfter, minPx }
 *   { type:'gap', px } · { type:'break' } · { type:'hr' } · { type:'table', rows }
 */
export function parseManualHtml(html) {
    const doc = new DOMParser().parseFromString(String(html || ''), 'text/html');
    const blocks = [];

    const paragraph = (el, extra = {}) => {
        const d = blockDefaults(el);
        const runs = [];
        collectRuns(el, { ...d.base }, runs);
        const { text, marks, base } = toTextAndMarks(runs);
        const tag = el.tagName.toLowerCase();
        if (!text) {
            // An empty paragraph still takes its line (the export gives <p> min-height:1.9em).
            blocks.push({ type: 'gap', px: Math.max(d.lh, 1.9) * d.base.px });
            return;
        }
        const heading = !!HEADING_EM[tag];
        const em = d.base.px;
        blocks.push({
            type: 'para',
            text,
            marks,
            base: base || d.base,
            lh: d.lh,
            align: d.align,
            indent: d.indent + (extra.indent || 0),
            bullet: extra.bullet || null,
            gapBefore: heading ? 0.5 * em : (extra.gapBefore || 0),
            gapAfter: heading ? 0.3 * em : (extra.gapAfter || 0),
            // <p> keeps a 1.9em minimum height whatever its line height says.
            minPx: tag === 'p' ? 1.9 * d.base.px : 0,
        });
    };

    const tableBlock = (el) => {
        // Rows, merged cells (colspan / rowspan) and header bolding come from the shared table parser.
        const parsed = parseHtmlTable(el.outerHTML);
        if (parsed) blocks.push({ type: 'table', parsed });
    };

    const hasBlockChild = (el) => [...el.children].some((c) => BLOCK_TAGS.has(c.tagName.toLowerCase()));

    const list = (el, depth = 0) => {
        const ordered = el.tagName.toLowerCase() === 'ol';
        let n = 0;
        blocks.push({ type: 'gap', px: 0.4 * BASE_PX });
        for (const li of el.children) {
            if (li.tagName.toLowerCase() !== 'li') continue;
            n += 1;
            const marker = ordered ? `${n}.` : (depth % 2 ? '◦' : '•');
            // Nested lists are walked after the item's own text.
            const clone = li.cloneNode(true);
            clone.querySelectorAll('ul, ol').forEach((nested) => nested.remove());
            paragraph(clone, { bullet: marker, indent: (1.5 + depth * 1.5) * BASE_PX });
            li.querySelectorAll(':scope > ul, :scope > ol').forEach((nested) => list(nested, depth + 1));
        }
        blocks.push({ type: 'gap', px: 0.4 * BASE_PX });
    };

    const walk = (parent) => {
        for (const node of parent.childNodes) {
            if (node.nodeType === 3) {
                if (node.nodeValue.trim()) {
                    const p = doc.createElement('p');
                    p.textContent = node.nodeValue;
                    paragraph(p);
                }
                continue;
            }
            if (node.nodeType !== 1) continue;
            const tag = node.tagName.toLowerCase();
            if (node.classList.contains('doc-page-break')) { blocks.push({ type: 'break' }); continue; }
            if (tag === 'table') { tableBlock(node); continue; }
            if (tag === 'ul' || tag === 'ol') { list(node); continue; }
            if (tag === 'hr') { blocks.push({ type: 'hr' }); continue; }
            if (tag === 'img' || tag === 'script' || tag === 'style' || tag === 'br') {
                if (tag === 'br') blocks.push({ type: 'gap', px: BASE_PX * 1.9 });
                continue;
            }
            if (['div', 'section', 'article', 'header', 'footer', 'main', 'form', 'figure'].includes(tag)
                && hasBlockChild(node)) {
                walk(node);
                continue;
            }
            if (tag === 'blockquote') { paragraph(node, { indent: 0.8 * BASE_PX, gapBefore: 0.6 * BASE_PX, gapAfter: 0.6 * BASE_PX }); continue; }
            paragraph(node);
        }
    };
    walk(doc.body);
    return blocks;
}

/* --------------------------------------------------------------------- flow */

/** Character offset after each wrapped line of a text object (so a paragraph can be cut between lines). */
function lineEnds(obj) {
    const lines = wrapTextLines(obj);
    const text = obj.text;
    const ends = [];
    let pos = 0;
    for (const line of lines) {
        const lt = line.text;
        if (!lt) {
            if (text[pos] === '\n') pos += 1;
            ends.push(pos);
            continue;
        }
        // Skip what the wrap swallowed (the space it broke on, a paragraph break) before the line.
        let guard = 0;
        while (pos < text.length && !text.startsWith(lt, pos) && guard < 8) { pos += 1; guard += 1; }
        pos += lt.length;
        ends.push(Math.min(pos, text.length));
    }
    return { lines, ends };
}

const sliceMarks = (marks, from, to) => (marks || [])
    .map((m) => ({ ...m, start: Math.max(m.start, from) - from, end: Math.min(m.end, to) - from }))
    .filter((m) => m.end > m.start);

/** Load the faces the document is set in so the editor measures (and wraps) against the real metrics. */
async function loadDocumentFonts(objects) {
    if (document.fonts?.load) {
        await Promise.all([FAMILY, 'SolaimanLipi', CATALOGUE_FAMILY].flatMap((fam) => [
            document.fonts.load(`400 16px "${fam}"`).catch(() => {}),
            document.fonts.load(`700 16px "${fam}"`).catch(() => {}),
        ]));
    }
    await ensureEditorFonts(objects);
}

/**
 * @param {{ html: string, name?: string,
 *   page: { width: number, height: number },
 *   margins: { top: number, right: number, bottom: number, left: number },
 *   stamp?: boolean }} args  page + margins in CSS px, exactly as the export reads them.
 * @returns {Promise<{ file: File, objects: object[], pages: {w:number,h:number}[],
 *   pageMargins: { top: number, bottom: number } }>}
 */
export async function manualHtmlToDoc({ html, name = 'document', page, margins, stamp = false }) {
    const pageW = page.width * PX;
    const pageH = page.height * PX;
    const left = margins.left * PX;
    const top = margins.top * PX;
    const bottom = margins.bottom * PX;
    const contentW = Math.max(60, pageW - left - margins.right * PX);
    const limit = pageH - bottom;

    const blocks = parseManualHtml(html);

    const textObject = (b, x, w, text, marks) => {
        const o = makeText({ x, y: 0, w, h: 10 }, {
            ...DEFAULT_TOOL_SETTINGS.text,
            fontFamily: CATALOGUE_FAMILY,
            fontStack: STACK,
            fontLabel: FAMILY,
            fontSize: Math.max(5, b.base.px * PX),
            color: '#1A1A1A',
            bold: !!b.base.bold,
            italic: !!b.base.italic,
            underline: !!b.base.underline,
            align: b.align,
            lineHeight: b.lh,
        }, text);
        if (b.base.strike) o.strike = true;
        if (marks?.length) o.marks = marks;
        return o;
    };

    // Warm the fonts first: wrapping and heights are measured with canvas metrics.
    const probe = blocks.filter((b) => b.type === 'para').slice(0, 1)
        .map((b) => textObject(b, 0, contentW, b.text, null));
    await loadDocumentFonts(probe);

    const pages = [{ w: pageW, h: pageH }];
    const objects = [];
    let pageIdx = 0;
    let y = top;
    const pageHasContent = () => objects.some((o) => o.page === pageIdx);
    const newPage = () => {
        pageIdx += 1;
        pages.push({ w: pageW, h: pageH });
        y = top;
    };

    for (const b of blocks) {
        if (b.type === 'break') {
            if (pageHasContent()) newPage();
            continue;
        }
        if (b.type === 'gap') { y += b.px * PX; continue; }
        if (b.type === 'hr') {
            if (y + 8 > limit && pageHasContent()) newPage();
            y += 4 * PX * 2;
            const line = makeShape(OBJ.LINE, { x: left, y, w: contentW, h: 0 }, {
                stroke: '#CCCCCC', strokeWidth: 0.75, fill: 'transparent', flipX: false, flipY: false,
            });
            line.page = pageIdx;
            objects.push(line);
            y += 4 * PX * 2;
            continue;
        }

        if (b.type === 'table') {
            const { parsed } = b;
            const t = makeTable({ x: left, y, w: contentW }, parsed.rows, parsed.cols, {
                fontSize: BASE_PX * 0.92 * PX,
                fontFamily: CATALOGUE_FAMILY,
                fontStack: STACK,
                lineHeight: 1.5,
                color: '#1A1A1A',
                stroke: '#AAAAAA',
                strokeWidth: 0.75,
                padX: 8 * PX,
                padY: 5 * PX,
            });
            t.cells = parsed.cells.map((r) => r.slice());
            if (parsed.merges.length) t.merges = parsed.merges;
            const bold = {};
            for (const [key, st] of Object.entries(parsed.styles)) if (st.bold) bold[key] = { bold: true };
            if (Object.keys(bold).length) t.cellStyles = bold;
            const sized = recomputeTable(t);
            y += 0.6 * BASE_PX * PX;
            if (y + sized.h > limit && pageHasContent()) newPage();
            sized.x = left;
            sized.y = y;
            sized.page = pageIdx;
            objects.push(sized);
            y += sized.h + 0.6 * BASE_PX * PX;
            continue;
        }

        // A paragraph (or list item / heading). The box is TEXT_PAD wider than the text on each side.
        const indent = b.indent * PX;
        const x = left + indent - TEXT_PAD;
        const w = Math.max(40, contentW - indent) + TEXT_PAD * 2;
        y += b.gapBefore * PX;
        let text = b.bullet ? `${b.bullet} ${b.text}` : b.text;
        let marks = b.bullet
            ? b.marks.map((m) => ({ ...m, start: m.start + b.bullet.length + 1, end: m.end + b.bullet.length + 1 }))
            : b.marks;

        // Place it, cutting it between lines when it runs off the page.
        // eslint-disable-next-line no-constant-condition
        while (true) {
            const obj = textObject(b, x, w, text, marks);
            const full = measuredTextHeight(obj) - TEXT_PAD * 2;
            if (y + full <= limit) {
                obj.y = y - TEXT_PAD;
                obj.h = full + TEXT_PAD * 2;
                obj.page = pageIdx;
                objects.push(obj);
                y += full;
                const min = b.minPx * PX;
                if (full < min) y += min - full;
                break;
            }
            const { lines, ends } = lineEnds(obj);
            const lineH = (i) => (lines[i].fontSize || obj.fontSize) * (obj.lineHeight || 1.35);
            let used = 0;
            let fit = 0;
            while (fit < lines.length && y + used + lineH(fit) <= limit) { used += lineH(fit); fit += 1; }
            // Keep at least two lines on each side of a cut (no stranded first/last line).
            if (lines.length - fit < 2) fit = Math.max(0, lines.length - 2);
            if (fit < 2 || fit >= lines.length) {
                // Too little room here — start the paragraph on a fresh page; on an empty page force it.
                if (pageHasContent()) { newPage(); continue; }
                fit = Math.min(lines.length, Math.max(1, fit));
                if (fit >= lines.length) {
                    obj.y = y - TEXT_PAD; obj.h = full + TEXT_PAD * 2; obj.page = pageIdx;
                    objects.push(obj); y += full;
                    break;
                }
            }
            const cut = ends[fit - 1];
            const head = text.slice(0, cut).replace(/\s+$/, '');
            const headObj = textObject(b, x, w, head, sliceMarks(marks, 0, head.length));
            const headH = measuredTextHeight(headObj) - TEXT_PAD * 2;
            headObj.y = y - TEXT_PAD;
            headObj.h = headH + TEXT_PAD * 2;
            headObj.page = pageIdx;
            objects.push(headObj);
            const rest = text.slice(cut);
            const lead = rest.length - rest.replace(/^\s+/, '').length;
            marks = sliceMarks(marks, cut + lead, text.length);
            text = rest.slice(lead);
            newPage();
            if (!text) break;
        }
        y += b.gapAfter * PX;
    }

    // Multi-page stamp documents carry "পাতা নং-N" at the top and "চলমান-N" at the bottom of each page.
    if (stamp && pages.length > 1) {
        const toBn = (n) => String(n).replace(/[0-9]/g, (d) => '০১২৩৪৫৬৭৮৯'[d]);
        const caption = (label, yPt) => {
            const o = makeText({ x: 0, y: yPt, w: pageW, h: 14 * 1.4 * PX + TEXT_PAD * 2 }, {
                ...DEFAULT_TOOL_SETTINGS.text,
                fontFamily: CATALOGUE_FAMILY,
                fontStack: STACK,
                fontLabel: FAMILY,
                fontSize: 14 * PX,
                color: '#222222',
                align: 'center',
                lineHeight: 1.4,
            }, label);
            o.role = STAMP_PAGE_ROLE;
            return o;
        };
        pages.forEach((_, i) => {
            if (i > 0) {
                const c = caption(`পাতা নং-${toBn(i + 1)}`, Math.max(8, margins.top - 28) * PX - TEXT_PAD);
                c.page = i;
                objects.push(c);
            }
            if (i < pages.length - 1) {
                const c = caption(`চলমান-${toBn(i + 2)}`, pageH - Math.max(8, margins.bottom - 28) * PX - 14 * 1.4 * PX - TEXT_PAD);
                c.page = i;
                objects.push(c);
            }
        });
    }

    const doc = await PDFDocument.create();
    pages.forEach((p) => doc.addPage([p.w, p.h]));
    const bytes = await doc.save();
    const safe = String(name || 'document').replace(/[\\/:*?"<>|]+/g, ' ').trim() || 'document';
    const file = new File([bytes], `${safe}.pdf`, { type: 'application/pdf' });
    return { file, objects, pages, pageMargins: { top, bottom } };
}
